<template>
    <div class="watch-tri">
        <!-- ===== Mobile headers / panels ===== -->
        <div class="mobile-headers">
            <!-- Company header -->
            <div class="mobile-header" @click="toggleCompanyListMobile">
                <div class="title">
                    <span class="chevron">{{ showCompanyListMobile ? '▾' : '▸' }}</span>
                    <strong>{{ companyTitle || 'Select a company' }}</strong>
                </div>
                <small v-if="companyMeta">{{ companyMeta }}</small>
            </div>
            <div v-if="isMobile && showCompanyListMobile" class="mobile-panel scroll">
                <slot name="left"></slot>
            </div>

            <!-- Seed header -->
            <div v-if="companyTitle" class="mobile-header" @click="toggleSeedListMobile">
                <div class="title">
                    <span class="chevron">{{ showSeedListMobile ? '▾' : '▸' }}</span>
                    <strong>{{ seedTitle || 'Select a report' }}</strong>
                </div>
                <small v-if="seedMeta">{{ seedMeta }}</small>
            </div>
            <div v-if="isMobile && companyTitle && showSeedListMobile" class="mobile-panel scroll">
                <div class="toolbar">
                    <slot name="middle-toolbar">
                        <span class="hint">Actions (placeholder)</span>
                    </slot>
                </div>
                <slot name="middle"></slot>
            </div>
        </div>

        <!-- ===== Desktop columns ===== -->
        <div class="columns" :class="{ 'is-mobile': isMobile }">
            <!-- LEFT -->
            <section class="col left" v-show="!isMobile">
                <header class="col-header">
                    <!-- top label row -->
                    <div class="row-top">
                        <span class="col-label">{{ leftHeader }}</span>
                    </div>

                    <!-- existing 3-lane header grid -->
                    <div class="col-header-grid">
                        <div class="left-head">
                            <button v-if="enableBack" class="icon-btn dark" aria-label="Back" title="Back"
                                @click="$emit('back')">‹</button>
                        </div>

                        <div class="center-head">
                            <h3 v-if="!showLeftSearch" class="sr-only">{{ leftHeader }}</h3>
                            <div v-else class="search-wrap fill">
                                <input class="search-input" :placeholder="leftSearchPlaceholder || 'Search…'"
                                    v-model="leftSearchTerm" @input="$emit('left-search-change', leftSearchTerm)" />
                                <button class="icon-btn dark" aria-label="Close search" title="Close search"
                                    @click="closeLeftSearch">✕</button>
                            </div>
                        </div>

                        <div class="right-head toolbar">
                            <button v-if="enableLeftSearch && !showLeftSearch" class="icon-btn dark" aria-label="Search"
                                title="Search" @click="showLeftSearch = true">
                                <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                                    <circle cx="11" cy="11" r="6" fill="currentColor" />
                                    <rect x="16" y="16" width="6" height="2" rx="1" transform="rotate(45 16 16)"
                                        fill="currentColor" />
                                </svg>
                            </button>
                            <slot name="left-toolbar" v-if="!showLeftSearch"></slot>
                        </div>
                    </div>
                </header>

                <div class="col-body">
                    <slot name="left"></slot>
                </div>
            </section>

            <!-- MIDDLE -->
            <section class="col middle" v-show="!isMobile || (isMobile && companyTitle && !showCompanyListMobile)">
                <header class="col-header">
                    <!-- top label row -->
                    <div class="row-top">
                        <span class="col-label">{{ middleHeader }}</span>
                    </div>

                    <!-- controls row -->
                    <div class="toolbar">
                        <slot name="middle-toolbar"></slot>
                    </div>
                </header>

                <div class="col-body">
                    <template v-if="!companyTitle">
                        <slot name="middle-empty">
                            <div class="placeholder">Choose a company to see reports.</div>
                        </slot>
                    </template>
                    <template v-else>
                        <slot name="middle"></slot>
                    </template>
                </div>
            </section>

            <!-- RIGHT -->
            <section class="col right"
                v-show="!isMobile || (isMobile && seedTitle && !showCompanyListMobile && !showSeedListMobile)">
                <header class="col-header">
                    <!-- top label row -->
                    <div class="row-top">
                        <span class="col-label">{{ rightHeader }}</span>
                    </div>

                    <!-- optional tools row -->
                    <div class="toolbar">
                        <slot name="right-toolbar"></slot>
                    </div>
                </header>

                <div class="col-body">
                    <template v-if="!seedTitle">
                        <slot name="right-empty">
                            <div class="placeholder">Select a report to view details.</div>
                        </slot>
                    </template>
                    <template v-else>
                        <div class="detail-wrap">
                            <slot name="right"></slot>
                        </div>
                    </template>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';

export default {
    name: 'WatchTriPane',
    props: {
        companyTitle: { type: String, default: '' },
        companyMeta: { type: String, default: '' },
        seedTitle: { type: String, default: '' },
        seedMeta: { type: String, default: '' },

        leftHeader: { type: String, default: 'Seeds' },
        middleHeader: { type: String, default: 'Reports' },
        rightHeader: { type: String, default: 'Details' },

        mobileMaxWidth: { type: Number, default: 900 },

        /* NEW */
        enableBack: { type: Boolean, default: false },
        enableLeftSearch: { type: Boolean, default: false },
        leftSearchPlaceholder: { type: String, default: '' },
    },
    setup(props, { emit }) {
        // responsive
        const isMobile = ref(false);
        const handleResize = () => { isMobile.value = window.innerWidth <= props.mobileMaxWidth; };
        onMounted(() => { handleResize(); window.addEventListener('resize', handleResize); });
        onBeforeUnmount(() => window.removeEventListener('resize', handleResize));

        // mobile collapses
        const showCompanyListMobile = ref(true);
        const showSeedListMobile = ref(true);
        const toggleCompanyListMobile = () => { showCompanyListMobile.value = !showCompanyListMobile.value; };
        const toggleSeedListMobile = () => { showSeedListMobile.value = !showSeedListMobile.value; };

        const middleHeaderComputed = computed(() => props.companyTitle || props.middleHeader);

        // LEFT SEARCH
        const showLeftSearch = ref(false);
        const leftSearchTerm = ref('');
        const closeLeftSearch = () => {
            showLeftSearch.value = false;
            leftSearchTerm.value = '';
            emit('left-search-change', '');
        };

        return {
            isMobile,
            showCompanyListMobile, showSeedListMobile,
            toggleCompanyListMobile, toggleSeedListMobile,
            middleHeaderComputed,

            /* search state */
            showLeftSearch, leftSearchTerm, closeLeftSearch,
        };
    },
};
</script>


<style scoped>
.watch-tri {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
}

/* Mobile headers */
.mobile-headers {
    display: none;
}

.mobile-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: #0f172a;
    color: #fff;
    padding: 12px 14px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    cursor: pointer;
}

.mobile-header .title {
    display: flex;
    align-items: center;
    gap: 8px;
}

.mobile-header .chevron {
    font-size: 18px;
    line-height: 1;
}

/* Mobile scroll panels */
.mobile-panel.scroll {
    max-height: 50vh;
    overflow: auto;
    padding: 10px;
    background: #0f172a0d;
    border-bottom: 1px solid #e5e7eb;
}

/* Desktop columns grid fills remaining height */
.columns {
    flex: 1 1 auto;
    min-height: 0;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 12px;
    padding: 12px;
}

/* Column layout */
.col {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    min-height: 94vh;
    overflow: hidden;
}

.col-header {
    display: flex;
    align-items: top;
    /* justify-content: space-between; */
    padding: 10px 12px;
    min-height: 90px;
    /* <- fixes the jump */
    color: black;
    width: 100%
}

.icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    background: #fff;
    cursor: pointer;
    font-size: 18px;
    line-height: 1;
}

.icon-btn:hover {
    background: #f7f7f7;
}

.left-toolbar {
    gap: 8px;
    width: 20;
}

.left-head,
.toolbar {
    display: flex;
    min-height: 40px;
}

.left-header-row {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
}

.right-head,
.toolbar {
    justify-content: end;
}

/* let middle block (title OR search) take all free space */
.grow {
    flex: 1 1 auto;
    min-width: 0;
    /* allows input to shrink without pushing */
    display: flex;
    align-items: center;
    gap: 10px;
}

.title-text {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
}

.search-input {
    flex: 1 1 auto;
    min-width: 0;
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #c7d2fe;
    border-radius: 10px;
    outline: none;
}

.search-wrap {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.search-wrap input {
    padding: 8px 12px;
    border: 1px solid #c7d2fe;
    border-radius: 10px;
    outline: none;
}

.col-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
    padding: 8px 12px;
}

.toolbar {
    width: 100%;
    display: flex;
    gap: 8px;
    align-items: top;
}

.placeholder {
    padding: 18px;
    color: #6b7280;
}

.detail-wrap {
    padding: 2px;
}

.icon-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    border: 1px solid #e5e7eb;
    background: #fff;
    color: #111827;
    cursor: pointer;
    font-size: 18px;
    line-height: 1;
}

.icon-btn.dark {
    border-color: #0b1b35;
    background: #0f172a;
    /* dark background */
    color: #ffffff;
    /* visible icon/label */
}

.icon-btn.dark:hover {
    background: #132241;
}

.col-header-grid {
    display: flex;
    grid-template-columns: auto 1fr auto;
    gap: 12px;
    width: 100%
}

/* Ensure center lane can shrink/grow */
.center-head {
    min-width: 0;
}

/* Search row fills the center lane */
.search-wrap.fill {
    display: flex;
    align-items: center;
    gap: 8px;
}

.search-input {
    flex: 1 1 auto;
    min-width: 0;
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #c7d2fe;
    border-radius: 12px;
    outline: none;
}

/* Column headers now have two rows: label row + controls row */
.col-header {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 10px 12px;
    min-height: 120px;
}

/* top label row */
.row-top {
    display: flex;
    align-items: center;
    /* vertical center for the small label */
    justify-content: flex-start;
    min-height: 18px;
    padding-top: 5px;
    padding-bottom: 8px;
}

/* the small label style */
.col-label {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: .02em;
    color: #0f172a;
    opacity: 0.9;
}

/* your existing 3-lane layout for the LEFT header */
.col-header-grid {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    /* properly centers buttons/inputs */
    gap: 12px;
    width: 100%;
}

/* minor fix: 'top' is not a valid value; use flex-start */
.toolbar,
.left-head {
    display: flex;
    align-items: center;
    /* vertical alignment */
    gap: 8px;
}

.right-head.toolbar {
    justify-content: flex-end;
}

/* hide the h3 used for accessibility when label row is present */
.sr-only {
    position: absolute !important;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

.col.right .col-body,
.col.right .detail-wrap,
.col.right .detail-wrap * {
  color: #0f172a !important;     /* slate-900 */
  margin-top: 5px;
}

.col.right .detail-wrap strong {
  color: #0b1b35 !important;     /* a bit darker for labels */
  font-weight: 700;
}

.col.right .col-body {
  background: #fff;              /* ensure there’s no tinted backdrop */
}

/* Responsive */
@media (max-width: 900px) {
    .mobile-headers {
        display: block;
    }

    .columns {
        grid-template-columns: 1fr;
        padding: 8px;
        gap: 8px;
    }

    .columns.is-mobile .col.left,
    .columns.is-mobile .col.middle,
    .columns.is-mobile .col.right {
        display: none;
    }
}
</style>